import type { SyncSnapshot, Lead, Visit, Objection, Feature, FeatureRequest } from '../../../shared/types';
import type { OutboxOp } from './db';
import { leadIdentity, opBody } from './dependencies';
type Rows = { leads: Lead[]; visits: Visit[]; objections: Objection[]; features: Feature[]; feature_requests: FeatureRequest[] };
/** Failed and parked mutations own their local rows until explicit retry/discard. */
export const protectedSnapshot = (snap: SyncSnapshot, local: Rows, ops: OutboxOp[]): SyncSnapshot => {
  const leadIds = new Set(ops.flatMap(o => { const id = leadIdentity(o, ops); return id ? [id] : []; }));
  const merge = <T extends { id: string }>(key: keyof Rows, server: T[], rows: T[]): T[] => {
    const ids = new Set(ops.flatMap(o => {
      const prefix = `/api/${key.replace('_', '-')}`;
      const id = o.path === prefix ? opBody(o).id : o.path.startsWith(prefix + '/') ? o.path.slice(prefix.length + 1).split('/')[0] : undefined;
      return typeof id === 'string' ? [id] : [];
    }));
    const protectedRows = rows.filter(r => ids.has(r.id) || (key === 'leads' ? leadIds.has(r.id) : leadIds.has((r as T & { lead_id?: string }).lead_id ?? '')));
    const keep = new Set(protectedRows.map(r => r.id));
    // IDs explicitly deleted locally also suppress the server row until resolved.
    return [...server.filter(r => !keep.has(r.id) && !ids.has(r.id)), ...protectedRows];
  };
  return { ...snap,
    leads: merge('leads', snap.leads, local.leads), visits: merge('visits', snap.visits, local.visits),
    objections: merge('objections', snap.objections, local.objections), features: merge('features', snap.features ?? [], local.features),
    feature_requests: merge('feature_requests', snap.feature_requests ?? [], local.feature_requests),
  };
};
